import { useState } from 'react'
import { Image } from '@crystallize/react-image'

import { useBasket } from '@/app/[locale]/(basket)'
import type { NumericContent, ProductVariant, SingleLineContent } from '@/crystallize/types.generated'

import { getWasteIconBySku } from '@/app/[locale]/(common)/(waste-icons)/waste-icons'
import { formatAmountToNOK } from '@/lib/utils'
import { Button } from '@/app/[locale]/(ui)/button'
import { Info } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/app/[locale]/(ui)/popover'
import { useTranslations } from 'next-intl'

const InfoTooltip = ({ content }: any) =>
  content ? (
    <Popover>
      <PopoverTrigger>
        <Info size={20} />
      </PopoverTrigger>
      <PopoverContent>{content}</PopoverContent>
    </Popover>
  ) : null

const VariantItem = ({
  variant,
  onBuy,
  isUpdating,
  isCompany = false,
}: {
  variant: ProductVariant
  productPath: string
  onBuy: (variant: ProductVariant) => void
  isUpdating: boolean
  isCompany?: boolean
}) => {
  const t = useTranslations()
  const companyWasteIcon = getWasteIconBySku(variant.sku || '')
  const CompanyWasteIcon = companyWasteIcon?.component
  const variantDisplayName = variant.name?.startsWith('Waste_') ? t(variant.name) : variant.name

  const maxWeight = variant.components?.find((attr) => attr.id === 'max-weight')?.content as unknown as NumericContent
  const maxWeightInfo = variant.components?.find((attr) => attr.id === 'max-weight-extra-info')
    ?.content as unknown as SingleLineContent

  return (
    <div className="mt-[5px] flex items-center gap-3 rounded bg-[#F3F2E9] p-2">
      <div className="flex h-[50px] w-[50px] shrink-0 items-center justify-center">
        {CompanyWasteIcon ? (
          <CompanyWasteIcon className="h-10 w-10 rounded bg-white p-[5px]" />
        ) : (
          <Image
            url={companyWasteIcon?.url || variant?.firstImage?.url}
            alt={variant?.firstImage?.altText || variant?.name || ''}
            style={{
              background: '#fff',
              padding: '5px',
              borderRadius: '4px',
              width: '40px',
              height: '40px',
            }}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-3">
        <div className={`w-full min-w-0 sm:w-auto ${maxWeight ? 'sm:flex-1' : 'sm:flex-[2]'}`}>
          <p className="overflow-hidden break-words font-bold leading-[1.3]">{variantDisplayName}</p>
        </div>
        {maxWeight && (
          <div className="w-full min-w-0 sm:w-auto sm:flex-1">
            <p className="flex items-center gap-1 overflow-hidden break-words text-[0.875rem] leading-[1.3]">
              {`${t('Max_weight')}: ${maxWeight.number} ${maxWeight.unit}`}
              <InfoTooltip content={maxWeightInfo?.text} />
            </p>
          </div>
        )}
      </div>
      {!isCompany && (
        <p className="min-w-[80px] shrink-0 text-right font-bold">
          {formatAmountToNOK(variant?.priceVariants?.[0]?.price || 0)}
        </p>
      )}
      <button
        className={`h-[50px] w-[50px] shrink-0 rounded-[3px] border border-black bg-black text-[1.875rem] font-bold text-white transition-all duration-200 hover:bg-white hover:text-black disabled:cursor-default disabled:border-none disabled:bg-[rgba(0,0,0,0.12)] disabled:text-[rgba(0,0,0,0.26)] ${
          isUpdating ? 'opacity-70' : 'opacity-100'
        }`}
        onClick={() => onBuy(variant)}
        disabled={isUpdating}
      >
        +
      </button>
    </div>
  )
}

export const ProductVariantsList = ({
  variants,
  productPath,
  sizePath,
  serviceType,
  productType,
  isCompany = false,
  ...rest
}: {
  variants: ProductVariant[]
  productPath: string
  sizePath?: string
  serviceType: 'PICKUP'
  productType: 'bag' | 'container'
  isCompany?: boolean
}) => {
  const t = useTranslations()
  const basket = useBasket()
  const [showAllVariants, setShowAllVariants] = useState(false)
  const [expandedSizePath, setExpandedSizePath] = useState<string | undefined>(undefined)

  const buy = (selectedVariant: ProductVariant) => {
    basket?.actions?.addItem({
      sku: selectedVariant.sku,
      path: productPath,
      sizePath,
      priceVariantIdentifier: 'default',
      serviceType,
      productType,
    })
    basket?.actions?.drawAttention(selectedVariant.sku, sizePath)
  }

  if (!variants) return null

  const isExpandedForCurrentSize = showAllVariants && expandedSizePath === sizePath
  const hasMoreVariants = variants.length > 12
  const visibleVariants = isExpandedForCurrentSize ? variants : variants.slice(0, 12)

  return (
    <div {...rest}>
      <p className="text-[1rem] font-bold">{t('Choose_waste_type')}</p>
      {visibleVariants.map((variant, index) => {
        const isThisButtonUpdating =
          basket?.status === 'server-basket-is-stale' &&
          basket?.attentionCartItem?.sku === variant.sku &&
          basket?.attentionCartItem?.sizePath === sizePath

        return (
          <VariantItem
            key={index}
            variant={variant}
            productPath={productPath}
            onBuy={buy}
            isUpdating={isThisButtonUpdating}
            isCompany={isCompany}
          />
        )
      })}
      {hasMoreVariants && !isExpandedForCurrentSize && (
        <div className="mt-6 flex justify-center">
          <Button
            variant="primary"
            onClick={() => {
              setShowAllVariants(true)
              setExpandedSizePath(sizePath)
            }}
          >
            {t('Show_more')}
          </Button>
        </div>
      )}
      {hasMoreVariants && isExpandedForCurrentSize && (
        <div className="mt-6 flex justify-center">
          <Button variant="primary" onClick={() => setShowAllVariants(false)}>
            {t('Show_less')}
          </Button>
        </div>
      )}
    </div>
  )
}
